// src/lib/charts/tooltip.tsx

"use client"

import { type ComponentType, type ReactElement, type ReactNode } from 'react'
import type { RechartsTooltipProps, ValueType, NameType } from '~/lib/charts/types'

/**
 * Represents a single data point in a tooltip payload.
 * 
 * @interface TooltipPayloadItem
 * @property {number} value - The numeric value of the data point
 * @property {string} name - The display name of the data series
 * @property {string} dataKey - The data key identifier used for this item
 * @property {string | undefined} [color] - Optional color for this data point (hex, rgb, etc.)
 * @property {Record<string, unknown> | undefined} [payload] - Original payload object from Recharts for consumers that need rich data
 * @property {string | number | Record<string, unknown> | undefined} [key: string] - Additional dynamic properties
 */
export interface TooltipPayloadItem {
  /** The value of the data point */
  value: number
  /** The name of the data series */
  name: string
  /** The data key used for this item */
  dataKey: string
  /** Optional color for this data point */
  color?: string
  /** Original payload object from Recharts for consumers that need rich data */
  payload?: Record<string, unknown>
  /** Additional properties */
  [key: string]: string | number | Record<string, unknown> | undefined
}

/**
 * Safely converts an unknown value to a Record<string, unknown> if it's an object.
 * 
 * @param {unknown} value - The value to convert
 * @returns {Record<string, unknown> | undefined} The converted record or undefined
 */
const toRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (value && typeof value === 'object') {
    return value as Record<string, unknown>
  }

  return undefined
}

/**
 * Safely converts an unknown value to a finite number.
 * Handles both numeric and string inputs, returning undefined for invalid values.
 * 
 * @param {unknown} value - The value to convert
 * @returns {number | undefined} The converted number or undefined if invalid
 */
const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number.parseFloat(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }

  return undefined
}

/**
 * Safely converts an unknown value to a non-empty trimmed string.
 * 
 * @param {unknown} value - The value to convert
 * @returns {string | undefined} The trimmed string or undefined if empty/invalid
 */
const toStringValue = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : undefined
  }

  return undefined
}

/**
 * Returns the first non-undefined string value from a list of candidates.
 * Each candidate is converted to a string using toStringValue.
 * 
 * @param {...unknown[]} values - The candidate values to check
 * @returns {string | undefined} The first valid string or undefined if none found
 */
const pickString = (...values: unknown[]): string | undefined => {
  for (const candidate of values) {
    const resolved = toStringValue(candidate)
    if (resolved !== undefined) {
      return resolved
    }
  }

  return undefined
}

/**
 * Props passed to custom tooltip content components in our chart wrappers.
 * This interface standardizes the props that tooltip components receive.
 * 
 * @interface CustomTooltipProps
 * @property {boolean | undefined} [active] - Whether the tooltip is currently active/visible
 * @property {TooltipPayloadItem[] | undefined} [payload] - Array of data points to display in the tooltip
 * @property {string | undefined} [label] - The label for the tooltip (typically the x-axis value)
 * @property {boolean} accessibilityLayer - Whether accessibility layer is enabled (Recharts 3.0+)
 * @property {Record<string, unknown> | undefined} [coordinate] - The coordinate position of the tooltip
 */
export interface CustomTooltipProps {
  /** Whether the tooltip is active */
  active?: boolean
  /** The payload data for the tooltip */
  payload?: TooltipPayloadItem[]
  /** The label for the tooltip */
  label?: string
  /** Whether accessibility layer is enabled (Recharts 3.0+) */
  accessibilityLayer: boolean
  /** The coordinate position of the tooltip */
  coordinate?: Record<string, unknown>
  /** Whether tooltip is shared across multiple data points */
  shared?: boolean
  /** Whether to include hidden data points */
  includeHidden?: boolean
  /** Whether to filter out null values */
  filterNull?: boolean
  /** Reverse direction configuration */
  reverseDirection?: { x?: boolean; y?: boolean }
  /** Allow escaping viewbox constraints */
  allowEscapeViewBox?: { x?: boolean; y?: boolean }
  /** Offset for tooltip positioning */
  offset?: number
  /** Custom position for tooltip */
  position?: { x?: number; y?: number }
  /** Trigger mechanism for tooltip */
  trigger?: 'hover' | 'click'
  /** Axis ID for tooltip */
  axisId?: string | number
}

let tooltipWrapperCache = new WeakMap<
  ComponentType<CustomTooltipProps>,
  (props: RechartsTooltipProps<ValueType, NameType>) => ReactNode
>()

/**
 * Create or re-use a tooltip wrapper for the provided component.
 *
 * Recharts 3 re-renders chart internals on every pointer event. Creating a new wrapper
 * function on each render forces React to unmount and mount the tooltip subtree repeatedly,
 * which is extremely costly for complex tooltips. By memoising the wrapper we ensure the
 * component identity stays stable across renders and avoid the expensive remount cost.
 * 
 * @param {ComponentType<CustomTooltipProps>} Component - The custom tooltip component to wrap
 * @returns {(props: RechartsTooltipProps<ValueType, NameType>) => ReactNode} The wrapped tooltip component
 */
export function getOrCreateTooltipWrapper(
  Component: ComponentType<CustomTooltipProps>
): (props: RechartsTooltipProps<ValueType, NameType>) => ReactNode {
  const cached = tooltipWrapperCache.get(Component)
  if (cached) {
    return cached
  }

  // Cache processed payloads by reference
  const payloadCache = new WeakMap<unknown[], TooltipPayloadItem[]>()
  let lastLabel: string | undefined

  const TooltipWrapper = (props: RechartsTooltipProps<ValueType, NameType>): ReactElement | null => {
    // Use type assertion to access properties that exist at runtime
    const rechartProps = props as unknown as {
      active?: boolean
      payload?: unknown[]
      label?: string
      accessibilityLayer?: boolean
      coordinate?: Record<string, unknown>
      shared?: boolean
      includeHidden?: boolean
      filterNull?: boolean
      reverseDirection?: { x?: boolean; y?: boolean }
      allowEscapeViewBox?: { x?: boolean; y?: boolean }
      offset?: number
      position?: { x?: number; y?: number }
      trigger?: 'hover' | 'click'
      axisId?: string | number
    }
    
    const { 
      active, 
      payload, 
      label, 
      accessibilityLayer = true,
      coordinate,
      shared,
      includeHidden,
      filterNull,
      reverseDirection,
      allowEscapeViewBox,
      offset,
      position,
      trigger,
      axisId
    } = rechartProps
    
    // Early bailout if not active
    if (!active || !Array.isArray(payload) || payload.length === 0) {
      return (
        <Component
          active={false}
          payload={[]}
          label={label}
          accessibilityLayer={accessibilityLayer}
          coordinate={coordinate}
          shared={shared}
          includeHidden={includeHidden}
          filterNull={filterNull}
          reverseDirection={reverseDirection}
          allowEscapeViewBox={allowEscapeViewBox}
          offset={offset}
          position={position}
          trigger={trigger}
          axisId={axisId}
        />
      )
    }

    // Check if we can reuse cached payload
    // Only reprocess if payload reference changed or label changed
    let typedPayload = payloadCache.get(payload)
    
    if (!typedPayload || lastLabel !== label) {
      // Process payload only if cache miss
      typedPayload = []

      for (const entry of payload) {
        if (!entry || typeof entry !== 'object') {
          continue
        }

        const raw = entry as Record<string, unknown>
        
        // Handle filterNull - skip entries with null/undefined values if filterNull is true
        if (filterNull && ('value' in raw && (raw.value === null || raw.value === undefined))) {
          continue
        }
        
        // Handle includeHidden - skip hidden entries if includeHidden is false
        if (includeHidden === false && 'hide' in raw && raw.hide === true) {
          continue
        }

        const numericValue = toNumber('value' in raw ? raw.value : undefined)

        if (numericValue === undefined) {
          continue
        }

        const nestedPayload = toRecord('payload' in raw ? raw.payload : undefined)
        const resolvedName =
          pickString(
            'name' in raw ? raw.name : undefined,
            nestedPayload ? nestedPayload.name : undefined,
            nestedPayload ? nestedPayload.label : undefined,
            'dataKey' in raw ? raw.dataKey : undefined,
            nestedPayload ? nestedPayload.dataKey : undefined
          ) ?? ''

        const resolvedKey =
          pickString(
            'dataKey' in raw ? raw.dataKey : undefined,
            nestedPayload ? nestedPayload.dataKey : undefined,
            resolvedName
          ) ?? 'value'

        const resolvedColor = toStringValue('color' in raw ? raw.color : undefined)
        const displayName = resolvedName === '' ? resolvedKey : resolvedName

        typedPayload.push({
          value: numericValue,
          name: displayName,
          dataKey: resolvedKey,
          color: resolvedColor,
          payload: nestedPayload
        })
      }
      
      payloadCache.set(payload, typedPayload)
    }

    lastLabel = label

    return (
      <Component
        active={true}
        payload={typedPayload}
        label={label}
        accessibilityLayer={accessibilityLayer}
        coordinate={coordinate}
        shared={shared}
        includeHidden={includeHidden}
        filterNull={filterNull}
        reverseDirection={reverseDirection}
        allowEscapeViewBox={allowEscapeViewBox}
        offset={offset}
        position={position}
        trigger={trigger}
        axisId={axisId}
      />
    )
  }

  TooltipWrapper.displayName = `TooltipWrapper(${Component.displayName ?? Component.name ?? 'Tooltip'})`
  tooltipWrapperCache.set(Component, TooltipWrapper)
  return TooltipWrapper
}

/**
 * Clears the tooltip wrapper cache. Used for testing purposes only.
 * 
 * @returns {void}
 */
export function __clearTooltipWrapperCacheForTests(): void {
  tooltipWrapperCache = new WeakMap()
}
